import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Search, Star, CheckCircle, MapPin, Phone,
  SlidersHorizontal, X, ChevronDown, ArrowLeft, Navigation
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const t = (lang, fr, en) => lang === 'fr' ? fr : en;

function getUserLocation() {
  try {
    const raw = localStorage.getItem('localsite_location');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function DirectoryBrowsePage() {
  const { language, toggleLanguage } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const userLoc = getUserLocation();

  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeCategory = searchParams.get('category') || '';
  const activeSubcategory = searchParams.get('subcategory') || '';
  const activeCity = searchParams.get('city') || '';
  const activeSearch = searchParams.get('search') || '';

  const [searchInput, setSearchInput] = useState(activeSearch);

  useEffect(() => {
    fetch(`${API}/api/directory/categories`)
      .then(r => r.json())
      .then(d => {
        setCategories(d.categories || []);
        setSubcategories(d.subcategories || []);
      })
      .catch(() => {});
  }, []);

  const loadBusinesses = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    if (activeSubcategory) params.set('subcategory', activeSubcategory);
    if (activeCity) params.set('city', activeCity);
    if (activeSearch) params.set('search', activeSearch);
    if (userLoc?.city && !activeCity) params.set('local_city', userLoc.city);
    params.set('sort', 'rating');
    params.set('limit', '50');
    try {
      const res = await fetch(`${API}/api/directory/businesses?${params.toString()}`);
      const data = await res.json();
      setBusinesses(data.businesses || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [activeCategory, activeSubcategory, activeCity, activeSearch, userLoc?.city]);

  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) { p.set(key, value); } else { p.delete(key); }
    if (key === 'category') { p.delete('subcategory'); }
    setSearchParams(p);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilter('search', searchInput.trim());
  };

  const clearAll = () => {
    setSearchParams({});
    setSearchInput('');
  };

  const hasFilters = activeCategory || activeSubcategory || activeCity || activeSearch;

  const filteredSubcats = activeCategory
    ? subcategories.filter(s => s.parent === activeCategory)
    : subcategories;

  const cities = [...new Set(businesses.map(b => b.city))].sort();
  const allCities = [...new Set(businesses.map(b => b.city).filter(Boolean))].sort();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Satoshi', 'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/60" data-testid="browse-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="browse-back-home">
              <ArrowLeft className="w-5 h-5 text-[#4B5563]" />
              <div className="w-8 h-8 bg-[#002FA7] rounded-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-[#0A0A0A] hidden sm:inline" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                LocalSite<span className="text-[#002FA7]">.Club</span>
              </span>
            </Link>

            <form onSubmit={handleSearch} className="flex-1 max-w-xl relative" data-testid="browse-search-form">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t(language, 'Rechercher...', 'Search...')}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-100 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40"
                data-testid="browse-search-input" />
            </form>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex bg-gray-100 rounded-md overflow-hidden text-xs font-semibold">
                <button onClick={() => language !== 'fr' && toggleLanguage()}
                  className={`px-2.5 py-1.5 transition-colors ${language === 'fr' ? 'bg-[#002FA7] text-white' : 'text-gray-500'}`}>FR</button>
                <button onClick={() => language !== 'en' && toggleLanguage()}
                  className={`px-2.5 py-1.5 transition-colors ${language === 'en' ? 'bg-[#002FA7] text-white' : 'text-gray-500'}`}>EN</button>
              </div>
              <button onClick={() => setFiltersOpen(!filtersOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-[#4B5563] hover:bg-gray-50 sm:hidden"
                data-testid="mobile-filter-toggle">
                <SlidersHorizontal className="w-4 h-4" /> {t(language, 'Filtres', 'Filters')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className={`${filtersOpen ? 'fixed inset-0 z-40 bg-white p-6 overflow-y-auto' : 'hidden'} sm:block sm:relative sm:w-60 flex-shrink-0`}
            data-testid="filters-sidebar">
            <div className="flex items-center justify-between mb-6 sm:hidden">
              <h3 className="font-bold text-lg">{t(language, 'Filtres', 'Filters')}</h3>
              <button onClick={() => setFiltersOpen(false)}><X className="w-5 h-5" /></button>
            </div>

            {hasFilters && (
              <button onClick={clearAll} className="text-sm text-[#002FA7] font-semibold mb-4 hover:underline" data-testid="clear-all-filters">
                {t(language, 'Effacer les filtres', 'Clear all filters')}
              </button>
            )}

            {/* Categories */}
            <div className="mb-6">
              <h4 className="font-semibold text-xs uppercase tracking-wider text-[#4B5563] mb-3">
                {t(language, 'Categories', 'Categories')}
              </h4>
              <div className="space-y-1">
                <button onClick={() => setFilter('category', '')}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeCategory ? 'bg-[#002FA7] text-white font-semibold' : 'text-[#4B5563] hover:bg-gray-100'}`}
                  data-testid="filter-all-categories">
                  {t(language, 'Toutes', 'All')}
                </button>
                {categories.map(cat => (
                  <button key={cat.key} onClick={() => setFilter('category', cat.key)}
                    className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeCategory === cat.key ? 'bg-[#002FA7] text-white font-semibold' : 'text-[#4B5563] hover:bg-gray-100'}`}
                    data-testid={`filter-category-${cat.key}`}>
                    {language === 'fr' ? cat.name_fr : cat.name_en}
                  </button>
                ))}
              </div>
            </div>

            {/* Subcategories */}
            {filteredSubcats.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-[#4B5563] mb-3">
                  {t(language, 'Sous-categories', 'Subcategories')}
                </h4>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {filteredSubcats.map(sub => (
                    <button key={sub.key} onClick={() => setFilter('subcategory', activeSubcategory === sub.key ? '' : sub.key)}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeSubcategory === sub.key ? 'bg-[#002FA7] text-white font-semibold' : 'text-[#4B5563] hover:bg-gray-100'}`}
                      data-testid={`filter-sub-${sub.key}`}>
                      {language === 'fr' ? sub.name_fr : sub.name_en}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cities Filter */}
            {cities.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-[#4B5563] mb-3">
                  {t(language, 'Villes', 'Cities')}
                </h4>
                <div className="space-y-1">
                  <button onClick={() => setFilter('city', '')}
                    className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeCity ? 'bg-[#002FA7] text-white font-semibold' : 'text-[#4B5563] hover:bg-gray-100'}`}
                    data-testid="filter-all-cities">
                    {t(language, 'Toutes les villes', 'All cities')}
                  </button>
                  {/* Show user's city first if applicable */}
                  {userLoc?.city && cities.includes(userLoc.city) && (
                    <button onClick={() => setFilter('city', activeCity === userLoc.city ? '' : userLoc.city)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-1.5 ${activeCity === userLoc.city ? 'bg-[#002FA7] text-white font-semibold' : 'text-[#002FA7] bg-[#002FA7]/5 hover:bg-[#002FA7]/10 font-medium'}`}
                      data-testid="filter-city-local">
                      <Navigation className="w-3 h-3" /> {userLoc.city}
                    </button>
                  )}
                  {cities.filter(c => c !== userLoc?.city).map(city => (
                    <button key={city} onClick={() => setFilter('city', activeCity === city ? '' : city)}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeCity === city ? 'bg-[#002FA7] text-white font-semibold' : 'text-[#4B5563] hover:bg-gray-100'}`}
                      data-testid={`filter-city-${city.toLowerCase().replace(/\s/g, '-')}`}>
                      {city}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {/* Active filters */}
            <div className="flex items-center gap-3 flex-wrap mb-6">
              <p className="text-sm text-[#4B5563]">
                <span className="font-bold text-[#0A0A0A]">{total}</span> {t(language, 'resultats', 'results')}
              </p>
              {activeCategory && (
                <span className="inline-flex items-center gap-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold px-3 py-1 rounded-full">
                  {language === 'fr' ? categories.find(c => c.key === activeCategory)?.name_fr : categories.find(c => c.key === activeCategory)?.name_en}
                  <button onClick={() => setFilter('category', '')} data-testid="remove-category-filter"><X className="w-3 h-3" /></button>
                </span>
              )}
              {activeSubcategory && (
                <span className="inline-flex items-center gap-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold px-3 py-1 rounded-full">
                  {language === 'fr' ? subcategories.find(s => s.key === activeSubcategory)?.name_fr : subcategories.find(s => s.key === activeSubcategory)?.name_en}
                  <button onClick={() => setFilter('subcategory', '')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {activeSearch && (
                <span className="inline-flex items-center gap-1 bg-gray-100 text-[#4B5563] text-xs font-semibold px-3 py-1 rounded-full">
                  "{activeSearch}"
                  <button onClick={() => { setFilter('search', ''); setSearchInput(''); }}><X className="w-3 h-3" /></button>
                </span>
              )}
              {activeCity && (
                <span className="inline-flex items-center gap-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold px-3 py-1 rounded-full">
                  <MapPin className="w-3 h-3" /> {activeCity}
                  <button onClick={() => setFilter('city', '')} data-testid="remove-city-filter"><X className="w-3 h-3" /></button>
                </span>
              )}
              {!activeCity && userLoc && (
                <span className="inline-flex items-center gap-1 text-[#002FA7] text-xs font-medium px-2 py-1 rounded-full bg-[#002FA7]/5">
                  <Navigation className="w-3 h-3" /> {t(language, `Trie par proximite : ${userLoc.city}`, `Sorted by proximity: ${userLoc.city}`)}
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-3 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : businesses.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="business-results-grid">
                {businesses.map((biz) => {
                  const isLocal = userLoc && biz.city?.toLowerCase() === userLoc.city?.toLowerCase();
                  return (
                  <Link key={biz.slug} to={`/business/${biz.slug}`}
                    className={`group bg-white rounded-2xl overflow-hidden border hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${isLocal ? 'border-[#002FA7]/30 ring-1 ring-[#002FA7]/10' : 'border-gray-100'}`}
                    data-testid={`browse-card-${biz.slug}`}>
                    <div className="relative h-44 overflow-hidden">
                      <img src={biz.image_url} alt={biz.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute top-3 left-3 flex gap-2">
                        {biz.rating > 0 && (
                          <span className="flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold">
                            <Star className="w-3.5 h-3.5 text-[#FFB300] fill-[#FFB300]" /> {biz.rating}
                          </span>
                        )}
                        {isLocal && (
                          <span className="flex items-center gap-1 bg-[#002FA7]/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-white">
                            <Navigation className="w-3 h-3" /> {t(language, 'Local', 'Local')}
                          </span>
                        )}
                      </div>
                      {biz.is_verified ? (
                        <span className="absolute top-3 right-3 flex items-center gap-1 bg-[#10B981]/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-white">
                          <CheckCircle className="w-3.5 h-3.5" /> {t(language, 'Verifie', 'Verified')}
                        </span>
                      ) : (
                        <span className="absolute top-3 right-3 flex items-center gap-1 bg-[#6B7280]/80 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-white">
                          {t(language, 'Invite', 'Invited')}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <span className="inline-block text-xs font-medium text-[#002FA7] bg-[#002FA7]/8 rounded-full px-3 py-1 mb-2">
                        {language === 'fr'
                          ? (subcategories.find(s => s.key === biz.subcategory)?.name_fr || categories.find(c => c.key === biz.category)?.name_fr || biz.category)
                          : (subcategories.find(s => s.key === biz.subcategory)?.name_en || categories.find(c => c.key === biz.category)?.name_en || biz.category)
                        }
                      </span>
                      <h3 className="font-bold text-[#0A0A0A] text-base mb-1">{biz.name}</h3>
                      <p className="text-xs text-[#4B5563] mb-2 line-clamp-2">
                        {language === 'fr' ? biz.description_fr : biz.description_en}
                      </p>
                      <div className="flex items-center gap-1.5 text-sm text-[#4B5563] mb-1">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        {biz.city}
                        {biz.neighborhood && <span className="text-gray-400">&middot; {biz.neighborhood}</span>}
                      </div>
                      {biz.phone && (
                        <div className="flex items-center gap-1.5 text-sm text-[#4B5563]">
                          <Phone className="w-3.5 h-3.5 flex-shrink-0" /> {biz.phone}
                        </div>
                      )}
                    </div>
                  </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#4B5563] font-medium">{t(language, 'Aucun resultat trouve', 'No results found')}</p>
                <p className="text-sm text-gray-400 mt-1">{t(language, 'Essayez de modifier vos filtres', 'Try adjusting your filters')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
